import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import type {
    PlansComparisonTableRequest,
    PlansComparisonTableResponse,
    MetricType,
} from '../../types';

const fetchPlansComparisonTable = async (
    payload: PlansComparisonTableRequest,
    signal?: AbortSignal,
): Promise<PlansComparisonTableResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}
    const response = await axios(tpoGenUrls.getPlansComparisonTableData, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
        signal,
    });
    return response.data;
};

interface UseScenarioKpiDataArgs {
    basePlanId: string | null;
    comparisonPlanIds: string[];
    kpiList: string[];
    metricType: MetricType;
    timePeriod?: 'full_year' | 'ytd' | 'ytg';
    searchKey?: string;
}

interface UseScenarioKpiDataResult {
    data: PlansComparisonTableResponse | null;
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
}

const useScenarioKpiData = ({
    basePlanId,
    comparisonPlanIds,
    kpiList,
    metricType,
    timePeriod = 'full_year',
    searchKey,
}: UseScenarioKpiDataArgs): UseScenarioKpiDataResult => {
    const enabled = !!basePlanId && kpiList.length > 0;
    const trimmedSearch = searchKey?.trim() || '';

    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.plansComparisonTable(
            basePlanId ?? '',
            comparisonPlanIds,
            kpiList,
            metricType,
            trimmedSearch,
        ),
        queryFn: ({ signal }) => fetchPlansComparisonTable({
            base_plan_id: basePlanId!,
            comparison_plan_ids: comparisonPlanIds,
            kpi_list: kpiList,
            time_period: timePeriod,
            current_date: Math.floor(Date.now() / 1000),
            metric_type: metricType,
            ...(trimmedSearch ? { search_key: trimmedSearch } : {}),
        }, signal),
        enabled,
        placeholderData: keepPreviousData,
    });

    return {
        data: data ?? null,
        isPending: enabled && isPending,
        isFetching,
        isError,
    };
};

export default useScenarioKpiData;
